import React from 'react';

// material components
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Drawer from '@material-ui/core/Drawer';
import { DialogContentText, IconButton, Tooltip } from '@material-ui/core';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';

// custom components
import AlertsCard from './AlertsCard';

// constants
import {
  ALERT_DRAWER_HEADING,
  CLOSE,
  NO_GLOBAL_ENTRIES_TEXT,
  NO_POINTS_ENTRIES_TEXT,
  POINT_NO_CLEAR_SUBJECT,
  TAB_GLOBAL,
  TAB_GLOBAL_LABEL,
  TAB_POINTS,
  TAB_POINTS_LABEL,
} from './constants';

// design tokens shared with the points-watching UI
import { C } from '../pointsWatching/constants';

// styling
import './AlertsDrawer.css';
import PropTypes from 'prop-types';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import ErrorFeedbackBar from '../global/ErrorFeedbackBar';
import { storeResponseMessage } from '../../redux/actions/settings';
import {
  responseMessageSelector,
  statusSelector,
} from '../../redux/selectors/global';
import withShipment from '../../withShipment';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { Dialog } from '@mui/material';

class AlertsDrawer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      alerts: [],
      responseMessage: '',
      feedbackBarOpen: false,
      expandingAlertId: '',
      expandingAlert: false,
      deleteDialogOpen: false,
      activeTab: TAB_GLOBAL,
    };
  }

  isPointAlert = (alert) => alert && alert.type === POINT_NO_CLEAR_SUBJECT;

  getTabbedAlerts = () => {
    const entries = this.props.alertEntries || [];
    const points = entries.filter(this.isPointAlert);
    const global = entries.filter((alert) => !this.isPointAlert(alert));
    return { global, points };
  };

  switchTab = (tab) => {
    if (tab !== this.state.activeTab) {
      this.setState({
        activeTab: tab,
        expandingAlertId: '',
        expandingAlert: false,
      });
    }
  };

  componentDidMount() {}

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevProps.responseMessage !== this.props.responseMessage &&
      this.props.responseMessage !== ''
    ) {
      this.setState({
        feedbackBarOpen: true,
      });
    }
  }

  closeFeedbackBar = () => {
    this.props.storeResponseMessage('');
    this.setState({
      feedbackBarOpen: false,
    });
  };

  viewSpecificAlert = (alertId) => {
    if (alertId !== this.state.expandingAlertId) {
      this.setState({
        expandingAlertId: alertId,
        expandingAlert: true,
      });
    } else {
      this.setState({
        expandingAlertId: '',
        expandingAlert: false,
      });
    }
  };

  openDeleteDialog = () => {
    this.setState({ deleteDialogOpen: true });
  };

  closeDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false });
  };

  confirmDeleteAll = () => {
    const isPointsTab = this.state.activeTab === TAB_POINTS;
    this.props.deleteAllAlert({
      locationId: this.props.user.selectedLocationId,
      type: POINT_NO_CLEAR_SUBJECT,
      // points tab clears only the unclear-image alerts; global clears everything else
      exclude: !isPointsTab,
    });
    this.setState({ deleteDialogOpen: false });
  };

  renderTabBar = (globalCount, pointsCount) => {
    const { activeTab } = this.state;
    const tab = (key, label, count) => {
      const active = activeTab === key;
      return (
        <button
          type="button"
          onClick={() => this.switchTab(key)}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            borderRadius: 8,
            backgroundColor: active ? C.navy2 : C.track,
            color: active ? '#fff' : C.ink,
            transition: 'background-color 0.2s ease',
          }}
        >
          {label}
          {count > 0 ? ` (${count})` : ''}
        </button>
      );
    };
    return (
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px' }}>
        {tab(TAB_GLOBAL, TAB_GLOBAL_LABEL, globalCount)}
        {tab(TAB_POINTS, TAB_POINTS_LABEL, pointsCount)}
      </div>
    );
  };

  render() {
    const { global: globalAlerts, points: pointAlerts } =
      this.getTabbedAlerts();
    const visibleAlerts =
      this.state.activeTab === TAB_POINTS ? pointAlerts : globalAlerts;
    const noVisibleAlerts = visibleAlerts.length === 0;
    const emptyText =
      this.state.activeTab === TAB_POINTS
        ? NO_POINTS_ENTRIES_TEXT
        : NO_GLOBAL_ENTRIES_TEXT;
    return (
      <Drawer
        className="drawer"
        anchor="right"
        open={this.props.alertDrawerOpen}
        transitionDuration={{ enter: 300, exit: 200 }}
        ModalProps={{
          keepMounted: true, // Improves performance
        }}
        onClose={this.props.handleAlertDrawerClose}
      >
        <ErrorFeedbackBar />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          severity={this.props.status?.success ? 'success' : 'error'}
          message={this.props.responseMessage}
          handleClose={this.closeFeedbackBar}
        />
        <div className="alerts-drawer">
          <div className="drawer-header">
            <div className="drawer-header-text">{ALERT_DRAWER_HEADING}</div>

            <div className="drawer-header-close-icon">
              <Tooltip title={CLOSE}>
                <IconButton aria-label="close" onClick={this.props.closeDrawer}>
                  <DoubleArrowIcon
                    variant="outlined"
                    className="drawer-close-icon"
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {this.renderTabBar(globalAlerts.length, pointAlerts.length)}

          {(this.props.user?.authority?.includes('ROLE_OWNER') ||
            this.props.user?.authority?.includes('ROLE_TECH_ADMIN') ||
            this.props.user?.authority?.includes('ROLE_MASTER_ADMIN')) && (
            <>
              {!noVisibleAlerts && (
                <div style={{}}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={this.openDeleteDialog}
                    fullWidth
                  >
                    {this.state.activeTab === TAB_POINTS
                      ? `Clear ${TAB_POINTS_LABEL} Notifications`
                      : `Clear ${TAB_GLOBAL_LABEL} Notifications`}
                  </Button>
                </div>
              )}

              {/* 👇 Confirmation Dialog */}
              <Dialog
                open={this.state.deleteDialogOpen}
                onClose={this.closeDeleteDialog}
              >
                <DialogTitle>Clear Notifications</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {this.state.activeTab === TAB_POINTS
                      ? `Are you sure you want to clear all "${TAB_POINTS_LABEL}" notifications?`
                      : `Are you sure you want to clear all "${TAB_GLOBAL_LABEL}" notifications?`}
                  </DialogContentText>
                </DialogContent>
                <DialogActions
                  style={{ justifyContent: 'center', marginBottom: 10 }}
                >
                  <Button variant="contained" onClick={this.closeDeleteDialog}>
                    Cancel
                  </Button>
                  <Button
                    onClick={this.confirmDeleteAll}
                    color="primary"
                    variant="contained"
                  >
                    Yes, Clear
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}

          {!noVisibleAlerts ? (
            visibleAlerts.map((alert) => {
              return (
                <AlertsCard
                  key={alert.alertId || alert._id}
                  alertEntry={alert}
                  snoozeAlert={this.props.snoozeAlert}
                  removeSnoozedAlert={this.props.removeSnoozedAlerts}
                  resolveAlert={this.props.resolveAlert}
                  expandingAlertId={this.state.expandingAlertId}
                  expandingAlert={this.state.expandingAlert}
                  viewSpecificAlert={this.viewSpecificAlert}
                  user={this.props.user}
                  onAlertNavigate={this.props.onAlertNavigate}
                />
              );
            })
          ) : (
            <Card>
              <CardContent>{emptyText}</CardContent>
            </Card>
          )}
        </div>
      </Drawer>
    );
  }
}

AlertsDrawer.propTypes = {
  alertDrawerOpen: PropTypes.bool,
  handleAlertDrawerClose: PropTypes.func,
  alertEntries: PropTypes.array,
  noAlerts: PropTypes.bool,
  closeDrawer: PropTypes.func,
};

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  responseMessage: responseMessageSelector(state),
});
const actionCreators = {
  storeResponseMessage,
};

AlertsDrawer.defaultProps = {
  alertDrawerOpen: false,
  handleAlertDrawerClose: () => {},
  alertEntries: [],
  noAlerts: true,
  closeDrawer: () => {},
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  AlertsDrawer
);
